/**
 * Validates and normalizes cached analysis data to ensure it matches expected structure
 * This prevents React errors when rendering components with invalid data
 */

/**
 * Validates and normalizes an analysis object
 * @param {Object} analysis - The analysis object to validate
 * @returns {Object|null} - Normalized analysis object or null if invalid
 */
export function validateAnalysis(analysis) {
  if (!analysis || typeof analysis !== 'object') {
    console.warn('[validateAnalysis] Analysis is not an object:', analysis);
    return null;
  }

  // Ensure all comment arrays are arrays
  const ensureArray = (value, defaultValue = []) => {
    if (Array.isArray(value)) return value;
    if (value == null) return defaultValue;
    console.warn('[validateAnalysis] Expected array, got:', typeof value, value);
    return defaultValue;
  };

  // Ensure all numbers are numbers
  const ensureNumber = (value, defaultValue = 0) => {
    if (typeof value === 'number' && !isNaN(value)) return value;
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      if (!isNaN(parsed)) return parsed;
    }
    return defaultValue;
  };

  // Normalize commentsResume/resume
  const normalizeResume = (resume) => {
    if (!resume) return null;
    
    // If it's a string (old format), convert to object
    if (typeof resume === 'string') {
      return {
        summary: resume,
        sentiment: 'neutral',
        insights: [],
        metrics: {}
      };
    }
    
    // If it's an object, ensure it has required properties
    if (typeof resume === 'object') {
      return {
        summary: resume.summary || 'No summary available',
        sentiment: resume.sentiment || 'neutral',
        sentimentIntensity: resume.sentimentIntensity || 'moderate',
        sentimentDescription: resume.sentimentDescription || '',
        insights: ensureArray(resume.insights, []),
        metrics: resume.metrics && typeof resume.metrics === 'object' ? resume.metrics : {}
      };
    }
    
    return null;
  };

  // Normalize revenueSignals
  const normalizeRevenueSignals = (revenueSignals) => {
    if (!revenueSignals || typeof revenueSignals !== 'object') {
      return {
        buyingIntentPercentage: 0,
        topBuyingSignals: [],
        mainObjections: [],
        buyingReadiness: 'Low'
      };
    }

    return {
      buyingIntentPercentage: ensureNumber(revenueSignals.buyingIntentPercentage, 0),
      topBuyingSignals: ensureArray(revenueSignals.topBuyingSignals, []).map(signal => ({
        text: signal.text || signal || '',
        score: ensureNumber(signal.score, 0)
      })),
      mainObjections: ensureArray(revenueSignals.mainObjections, []).map(objection => ({
        text: objection.text || objection || '',
        score: ensureNumber(objection.score, 0)
      })),
      buyingReadiness: revenueSignals.buyingReadiness || 'Low'
    };
  };

  // Validate and normalize comment objects in arrays
  const normalizeComments = (comments) => {
    if (!Array.isArray(comments)) return [];
    
    return comments.filter(comment => {
      // Ensure comment is an object
      if (!comment || typeof comment !== 'object') {
        return false;
      }
      
      // Ensure comment has at least one text property
      const hasText = comment.textDisplay || comment.textOriginal || comment.text;
      if (!hasText) {
        return false;
      }
      
      // Ensure comment has an id
      if (!comment.id) {
        console.warn('[validateAnalysis] Comment missing id:', comment);
        // Generate a temporary id if missing
        comment.id = `temp-${Date.now()}-${Math.random()}`;
      }
      
      return true;
    });
  };

  // Build normalized analysis object
  const normalized = {
    potentialBuyers: normalizeComments(ensureArray(analysis.potentialBuyers)),
    frictionPoints: normalizeComments(ensureArray(analysis.frictionPoints)),
    questions: normalizeComments(ensureArray(analysis.questions)),
    positiveFeedback: normalizeComments(ensureArray(analysis.positiveFeedback)),
    suggestions: normalizeComments(ensureArray(analysis.suggestions)),
    otherComments: normalizeComments(ensureArray(analysis.otherComments)),
    totalComments: ensureNumber(analysis.totalComments, 0),
    fetchedComments: ensureNumber(analysis.fetchedComments, 0),
    filteredCount: ensureNumber(analysis.filteredCount, 0),
    buyerCount: ensureNumber(analysis.buyerCount, 0),
    frictionCount: ensureNumber(analysis.frictionCount, 0),
    questionCount: ensureNumber(analysis.questionCount, 0),
    positiveCount: ensureNumber(analysis.positiveCount, 0),
    suggestionCount: ensureNumber(analysis.suggestionCount, 0),
  };

  // Handle resume/commentsResume (backward compatibility)
  const resume = analysis.resume || analysis.commentsResume;
  if (resume) {
    normalized.commentsResume = normalizeResume(resume);
    // Also set resume for backward compatibility
    normalized.resume = normalized.commentsResume;
  }

  // Handle revenueSignals
  if (analysis.revenueSignals) {
    normalized.revenueSignals = normalizeRevenueSignals(analysis.revenueSignals);
  }

  // Recalculate counts from arrays if they don't match
  if (normalized.buyerCount !== normalized.potentialBuyers.length) {
    normalized.buyerCount = normalized.potentialBuyers.length;
  }
  if (normalized.frictionCount !== normalized.frictionPoints.length) {
    normalized.frictionCount = normalized.frictionPoints.length;
  }
  if (normalized.questionCount !== normalized.questions.length) {
    normalized.questionCount = normalized.questions.length;
  }
  if (normalized.positiveCount !== normalized.positiveFeedback.length) {
    normalized.positiveCount = normalized.positiveFeedback.length;
  }
  if (normalized.suggestionCount !== normalized.suggestions.length) {
    normalized.suggestionCount = normalized.suggestions.length;
  }

  return normalized;
}

/**
 * Validates and normalizes cached data from analysisStorage
 * @param {Object} cached - Cached data object from loadAnalysis
 * @returns {Object|null} - Normalized cached data or null if invalid
 */
export function validateCachedData(cached) {
  if (!cached || typeof cached !== 'object') {
    console.warn('[validateCachedData] Cached data is not an object:', cached);
    return null;
  }

  // Validate analysis
  const analysis = validateAnalysis(cached.analysis);
  if (!analysis) {
    console.warn('[validateCachedData] Invalid analysis object');
    return null;
  }

  // Validate videoMetadata
  if (!cached.videoMetadata || typeof cached.videoMetadata !== 'object') {
    console.warn('[validateCachedData] Invalid videoMetadata');
    return null;
  }

  // Validate comments array
  const comments = Array.isArray(cached.comments) ? cached.comments : [];
  
  // Normalize comments to ensure they have required properties
  const normalizedComments = comments.map(comment => {
    if (!comment || typeof comment !== 'object') {
      return null;
    }
    
    // Ensure comment has text property
    if (!comment.textDisplay && !comment.textOriginal && !comment.text) {
      return null;
    }
    
    // Ensure comment has id
    if (!comment.id) {
      comment.id = `temp-${Date.now()}-${Math.random()}`;
    }
    
    return comment;
  }).filter(Boolean);

  return {
    analysis,
    videoMetadata: cached.videoMetadata,
    comments: normalizedComments,
    analyzedAt: cached.analyzedAt || Date.now()
  };
}

